import {combineEpics, Epic, ofType, StateObservable} from "redux-observable"
import {from, merge, of, zip} from "rxjs"
import {
    catchError,
    map,
    mergeMap,
    switchMap,
    tap,
    withLatestFrom,
} from "rxjs/operators"
import {getType} from "typesafe-actions"
import axios, {AxiosResponse} from "axios"
import * as _ from "lodash"
import qs from "qs"
import fileDownload from "js-file-download"
import {
    cancelReferenceRangesDataAction,
    cancelReferenceRangesDataSuccessAction,
    errorAction,
    exportDetectabilitySummaryAction,
    exportDisplayDataAction,
    loadAssayAction,
    loadAssaySuccessAction,
    loadClassesAction,
    loadClassesSuccessAction,
    loadConfigurationAction,
    loadConfigurationSuccessAction,
    loadDateTableDataAction,
    loadDateTableDataSuccessAction,
    loadDemographicsDataAction,
    loadDemographicsDataSuccessAction,
    loadDetectabilitySummaryDataAction,
    loadDetectabilitySummaryDataSuccessAction,
    loadExistingVariablesAction,
    loadExistingVariablesSuccessAction,
    loadMaterialsAction,
    loadMaterialsSuccessAction,
    loadMatricesAction,
    loadMatricesSuccessAction,
    loadMetabolitesAction,
    loadMetabolitesExternalAction,
    loadMetabolitesSuccessAction,
    loadProjectsAction,
    loadProjectsSuccessAction,
    loadReferenceRangesDataAction,
    loadReferenceRangesDataSuccessAction,
    loadReferenceRangesInternalDataAction,
    loadSpeciesAction,
    loadSpeciesSuccessAction,
    loadTotalNumberOfSamplesAction,
    loadTotalNumberOfSamplesSuccessAction,
    loadValuesAction,
    loadValuesSuccessAction,
    loadVariablesAction,
    loadVariablesSuccessAction,
} from "./refdb.actions"
import {RefDbDataContext} from "../models/displayDataContext"
import {State} from "./refdb.state"
import {DetectabilitySummary} from "../models/DetectabilitySummary"

const {CancelToken} = axios
let source = CancelToken.source()

const getContext = (metStatState: State): RefDbDataContext => {
    return metStatState.context
}

const getMultiContext = (metStatState: State): RefDbDataContext[] => {
    return metStatState.multiContext
}

function mergeRows(valid, invalidElement: any) {
    const countOfInvalid = invalidElement ? invalidElement.count : 0
    const total = valid.count + countOfInvalid
    const percent = (valid.count / total) * 100

    // only for debug:
    // valid.count = `${total}/${valid.count}/${countOfInvalid}`
    valid.count = total
    valid.lod = percent

    return valid
}

function itemKey(n) {
    return n.analyte + n.aClass
}

function getInvalidMeasures(response: AxiosResponse<any, any>) {
    const invalid = new Map(
        response.data
            .filter(
                (value) =>
                    value.status === "< LOD" || value.status === "Invalid"
            )
            .map((n) => [itemKey(n), n])
    )
    return invalid
}

function getValidMeasures(response: AxiosResponse<any, any>) {
    const valid = new Map(
        response.data
            .filter(
                (value) => value.status === "Valid" || value.status === "> ULOQ"
            )
            .map((n) => [itemKey(n), n])
    )
    return valid
}

function filter(response: AxiosResponse<any, any>, filterCriteria: string[]) {
    const valid = new Map(
        response.data
            .filter((value) => filterCriteria.includes(value.status))
            .map((n) => [n.analyte, n])
    )
    return valid
}

function margeValidAndInvalidSamples(response: AxiosResponse) {
    const invalid = getInvalidMeasures(response)

    const valid = getValidMeasures(response)
    const metaboliteClass = _.uniq(response.data.map((item) => itemKey(item)))

    return metaboliteClass.map((item) => {
        const invalidItem = invalid.get(item)
        const validItem = valid.get(item)

        if (invalidItem && validItem) {
            return mergeRows(validItem, invalidItem)
        }
        if (!invalidItem && validItem) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return {...validItem, lod: 100}
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return {...invalidItem, lod: 0}
    })
}

function margeValidAndInvalidSamplesDetectabilitySummary(
    response: AxiosResponse
) {
    const invalid = filter(response, ["< LOD", "Invalid"])
    const valid = filter(response, ["Valid", "> ULOQ"])
    return _.uniq(response.data.map((element) => element.analyte)).map(
        (analyte) =>
            invalid.has(analyte) && valid.has(analyte)
                ? mergeRows(valid.get(analyte), invalid.get(analyte))
                : {
                      analyte,
                      aClass: valid.has(analyte)
                          ? (valid.get(analyte) as DetectabilitySummary).aClass
                          : (invalid.get(analyte) as DetectabilitySummary)
                                .aClass,
                      lod: valid.has(analyte) ? 100 : 0,
                  }
    )
}

export const saveExportDisplayData = (data: Blob, filename: string) => {
    fileDownload(data, filename)
    return {
        type: "displayData/EXPORT_SUCCESS",
    }
}

const FILTER_TOKEN_SEPARATOR = ":"
const FILTER_ARRAY_VALUE_SEPARATOR = ";"

function convertArrayToParamList(params: any[]): string {
    let result = ""
    if (params.length > 1) {
        params.forEach((param) => {
            result += param + FILTER_ARRAY_VALUE_SEPARATOR
        })
        // strip our last ';'
        result = _.trimEnd(result, FILTER_ARRAY_VALUE_SEPARATOR)
    } else {
        result = params[0]
    }
    return result
}

function getFilterValue(value: any) {
    // eslint-disable-next-line no-nested-ternary
    return _.isArray(value)
        ? convertArrayToParamList(value)
        : _.isDate(value)
        ? (value as Date).toISOString()
        : value
}

function createFilterParams(filters: any): string[] {
    const result: string[] = []
    _.forOwn(filters, function (value, key) {
        const filterValue = value.value
        if (value.enable) {
            result.push(
                value.name +
                    FILTER_TOKEN_SEPARATOR +
                    (filterValue
                        ? encodeURIComponent(getFilterValue(filterValue))
                        : encodeURIComponent(getFilterValue(value.from)) +
                          FILTER_TOKEN_SEPARATOR +
                          encodeURIComponent(getFilterValue(value.to))) +
                    FILTER_TOKEN_SEPARATOR +
                    value.matchMode
            )
        }
    })
    return result
}

function getParamsFrom(multiContext: RefDbDataContext[]) {
    return multiContext.map((contex) => {
        return {
            material: contex.material,
            assay: contex.assay.enable ? contex.assay.value : undefined,
            species: contex.species,
            projects: contex.projects.enable
                ? contex.projects.value
                : undefined,
            classes: contex.classes.enable ? contex.classes.value : undefined,
            metabolites: contex.metabolites.enable
                ? contex.metabolites.value.map((value) => value.value)
                : undefined,
            filters: createFilterParams(contex.filters),
        }
    })
}

function getParams(state$: StateObservable<any>, subset = 0): any {
    const {
        projects,
        material,
        assay,
        species,
        classes,
        metabolites,
        filters,
        ownSamples,
    } = state$.value.core.user.internOnly
        ? state$.value.refDb.multiContext[subset]
        : getContext(state$.value.refDb)

    const {
        exportFileType,
        includesSummary,
        log2Transformation,
        categorizeDetectability,
        showHmdbId,
        sumUpMetaboliteClasses,
    } = getContext(state$.value.refDb)

    const {generateTable} = state$.value.refDb

    return {
        generateTable,
        projects: projects.enable ? projects.value : undefined,
        material,
        assay: assay.enable ? assay.value : undefined,
        species,
        showHmdbId,
        classes: classes.enable ? classes.value : undefined,
        metabolites: metabolites.enable
            ? metabolites.value.map((value) => value.value)
            : undefined,
        ownSamples,
        exportFileType,
        includesSummary,
        log2Transformation,
        categorizeDetectability,
        sumUpMetaboliteClasses,
        searchCriteria: getParamsFrom(state$.value.refDb.multiContext),
        filters: createFilterParams(filters).toString(),
    }
}

const loadSpeciesData: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(loadSpeciesAction)),
        withLatestFrom(state$),
        switchMap(() => from(axios.get<any[]>("/api/species"))),
        map((response) => {
            return response.data.map((item) => item.item)
        }),
        map((items) => {
            return loadSpeciesSuccessAction(items)
        }),
        catchError((err) => of(errorAction(err)))
    )

const loadMaterialsData: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(loadMaterialsAction)),
        withLatestFrom(state$),
        switchMap(() => from(axios.get<any[]>("/api/materials"))),
        map((response) => {
            return response.data.map((item) => item.item)
        }),
        map((items) => {
            return loadMaterialsSuccessAction(items)
        }),
        catchError((err) => of(errorAction(err)))
    )

const loadAssayData: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(loadAssayAction)),
        withLatestFrom(state$),
        switchMap(() => from(axios.get<any[]>("/api/assay"))),
        map((response) => {
            return response.data.map((item) => item.item)
        }),
        map((items) => {
            return loadAssaySuccessAction(items)
        }),
        catchError((err) => of(errorAction(err)))
    )

function tryToGetAssayFromMulticonext(state$: StateObservable<any>) {
    return getMultiContext(state$.value.refDb).length > 0
        ? getMultiContext(state$.value.refDb)[0].assay.value
        : undefined
}

const loadMetabolitesData: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(loadMetabolitesAction)),
        withLatestFrom(state$),
        switchMap(() =>
            from(
                axios.get<any[]>("/api/metabolites", {
                    params: {
                        assay: getContext(state$.value.refDb).assay.enable
                            ? getContext(state$.value.refDb).assay.value
                            : tryToGetAssayFromMulticonext(state$),
                    },
                    paramsSerializer: (params) => {
                        return qs.stringify(params, {arrayFormat: "repeat"})
                    },
                })
            )
        ),
        map((response) => {
            return loadMetabolitesSuccessAction(response.data)
        }),
        catchError((err) => of(errorAction(err)))
    )
const loadMetabolitesExternalData: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(loadMetabolitesExternalAction)),
        withLatestFrom(state$),
        switchMap(() =>
            from(
                axios.get<any>("/api/metabolitesExternal", {
                    headers: {"Content-Type": "text/event-stream"},
                })
            )
        ),
        map((response) => {
            return loadMetabolitesSuccessAction(response.data)
        }),
        catchError((err) => of(errorAction(err)))
    )

const loadValuesData: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(loadValuesAction)),
        withLatestFrom(state$),
        mergeMap((action) =>
            from(
                axios.get<any[]>(`/api/values?variable=${action[0].payload}`)
            ).pipe(
                map((response) => {
                    return {
                        params: action[0].payload,
                        result: response.data.map((item) => item.item),
                    }
                })
            )
        ),
        map(({params, result}) => {
            return loadValuesSuccessAction({params, result})
        }),
        catchError((error, caught) => merge(of(errorAction(error)), caught))
    )
const loadClassesData: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(loadClassesAction)),
        withLatestFrom(state$),
        switchMap(() =>
            from(
                axios.get<any[]>(`/api/classes`, {
                    params: {
                        assay: getContext(state$.value.refDb).assay.enable
                            ? getContext(state$.value.refDb).assay.value
                            : tryToGetAssayFromMulticonext(state$),
                    },
                    paramsSerializer: (params) => {
                        return qs.stringify(params, {arrayFormat: "repeat"})
                    },
                })
            )
        ),
        map((response) => {
            return response.data.map((item) => item.item)
        }),
        map((items) => {
            return loadClassesSuccessAction(items)
        }),
        catchError((error, caught) => merge(of(errorAction(error)), caught))
    )

const loadProjectsData: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(loadProjectsAction)),
        withLatestFrom(state$),
        switchMap(() => from(axios.get<any[]>("/api/projects"))),
        map((response) => {
            return response.data.map((item) => item.item)
        }),
        map((items) => {
            return loadProjectsSuccessAction(items)
        }),
        catchError((error, caught) => merge(of(errorAction(error)), caught))
    )

const loadMatrices: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(loadMatricesAction)),
        withLatestFrom(state$),
        switchMap(() => from(axios.get<any[]>("/api/matrices"))),
        map((response) => {
            return loadMatricesSuccessAction(response.data)
        }),
        catchError((error, caught) => merge(of(errorAction(error)), caught))
    )

const loadVariablesData: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(loadVariablesAction)),
        withLatestFrom(state$),
        switchMap(() => from(axios.get<any[]>("/api/variables"))),
        map((response) => {
            return response.data.map((item) => item.item)
        }),
        map((items) => {
            return loadVariablesSuccessAction(items)
        }),
        catchError((err) => of(errorAction(err)))
    )

const loadExistingVariables: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(loadExistingVariablesAction)),
        withLatestFrom(state$),
        switchMap(() => from(axios.get<any[]>("/api/existingVariables"))),
        map((response) => {
            return loadExistingVariablesSuccessAction(response.data)
        }),
        catchError((err) => of(errorAction(err)))
    )

const loadConfiguration: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(loadConfigurationAction)),
        withLatestFrom(state$),
        mergeMap((action, state) =>
            from(
                axios.get<any[]>(`/api/configuration`, {
                    params: {type: action[0].payload},
                })
            )
        ),
        map((response) =>
            loadConfigurationSuccessAction({
                data: response.data.map((item) => item.item),
                type: response.config.params.type.toLowerCase(),
            })
        ),
        catchError((error, caught) => {
            console.error(error)
            return merge(of(errorAction(error)), caught)
        })
    )

const loadDetectabilitySummaryData: Epic = (action$, state$) => {
    return action$.pipe(
        ofType(getType(loadDetectabilitySummaryDataAction)),
        withLatestFrom(state$),
        mergeMap((action) =>
            zip(
                from(
                    axios.get<any[]>(`/api/detectabilitySummary`, {
                        params: getParams(state$, action[0].payload),
                        paramsSerializer: (params) => {
                            return qs.stringify(params, {arrayFormat: "repeat"})
                        },
                    })
                ),
                of(action[0])
            )
        ),
        map(([response, initialAction]) => {
            return {
                response: margeValidAndInvalidSamplesDetectabilitySummary(
                    response
                ),
                action: initialAction,
            }
        }),
        map((result) => {
            return loadDetectabilitySummaryDataSuccessAction(result)
        }),
        catchError((error, caught) => merge(of(errorAction(error)), caught))
    )
}

const loadDemographicsData: Epic = (action$, state$) => {
    return action$.pipe(
        ofType(getType(loadDemographicsDataAction)),
        withLatestFrom(state$),
        switchMap((action) =>
            from(
                axios.get<any[]>(`/api/demographics`, {
                    cancelToken: source.token,
                    params: getParams(state$),
                    paramsSerializer: (params) => {
                        return qs.stringify(params, {arrayFormat: "repeat"})
                    },
                })
            )
        ),

        map((response) => {
            return loadDemographicsDataSuccessAction(response.data)
        }),
        catchError((error, caught) => merge(of(errorAction(error)), caught))
    )
}

const loadDateTableData: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(loadDateTableDataAction)),
        withLatestFrom(state$),
        switchMap(() =>
            from(
                axios.get<any[]>(`/api/dataTable`, {
                    params: getParams(state$),
                    paramsSerializer: (params) => {
                        return qs.stringify(params, {arrayFormat: "repeat"})
                    },
                })
            )
        ),
        map((response) => {
            return loadDateTableDataSuccessAction(response.data)
        }),
        catchError((error, caught) => merge(of(errorAction(error)), caught))
    )

const loadReferenceRangesData: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(loadReferenceRangesDataAction)),
        withLatestFrom(state$),
        switchMap((action) =>
            from(
                axios.get<any[]>(`/api/referenceRanges`, {
                    cancelToken: source.token,
                    params: getParams(state$),
                    paramsSerializer: (params) => {
                        return qs.stringify(params, {arrayFormat: "repeat"})
                    },
                })
            )
        ),
        map((response) => {
            return loadReferenceRangesDataSuccessAction(response.data)
        }),
        catchError((error, caught) => {
            if (axios.isCancel(error)) {
                console.log(
                    "Previous request canceled, new request is send",
                    error.message
                )
            }
            return merge(of(errorAction(error)), caught)
        })
    )
const loadReferenceRangesInternalData: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(loadReferenceRangesInternalDataAction)),
        withLatestFrom(state$),
        switchMap((action) =>
            from(
                axios.get<any[]>(`/api/referenceRangesInternal`, {
                    cancelToken: source.token,
                    params: getParams(state$),
                    paramsSerializer: (params) => {
                        return qs.stringify(params, {arrayFormat: "repeat"})
                    },
                })
            )
        ),
        map((response) => {
            return margeValidAndInvalidSamples(response)
        }),
        map((result) => {
            return loadReferenceRangesDataSuccessAction(result)
        }),
        catchError((error, caught) => {
            if (axios.isCancel(error)) {
                console.log(
                    "Previous request canceled, new request is send",
                    error.message
                )
            }
            return merge(of(errorAction(error)), caught)
        })
    )
const cancelReferenceRangesData: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(cancelReferenceRangesDataAction)),
        tap((action) => {
            source.cancel("Operation canceled by the user.")
            source = CancelToken.source()
        }),
        map(() => {
            return cancelReferenceRangesDataSuccessAction(false)
        }),
        catchError((error, caught) => merge(of(errorAction(error)), caught))
    )

function extractFileName(response: AxiosResponse<any>) {
    let filename
    const headerLine = response.headers["content-disposition"]
    if (headerLine) {
        const startFileNameIndex = headerLine.indexOf('"') + 1
        const endFileNameIndex = headerLine.lastIndexOf('"')
        filename = headerLine.substring(startFileNameIndex, endFileNameIndex)
    }
    return filename
}

const exportDisplayData: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(exportDisplayDataAction)),
        withLatestFrom(state$),
        switchMap((action) =>
            from(
                axios.get<Blob>(`/api/data/download${action[0].payload}`, {
                    params: getParams(state$),
                    paramsSerializer: (params) => {
                        return qs.stringify(params, {arrayFormat: "repeat"})
                    },
                    responseType: "blob",
                })
            ).pipe(
                map((response: AxiosResponse) => {
                    const filename = extractFileName(response)
                    return saveExportDisplayData(response.data, filename)
                }),
                catchError((err) => of(errorAction(err)))
            )
        )
    )

const exportDetectabilitySummary: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(exportDetectabilitySummaryAction)),
        withLatestFrom(state$),
        switchMap((action) =>
            from(
                axios.get<Blob>(
                    `/api/data/downloadDetectabilitySummaryInternal`,
                    {
                        params: getParams(state$),
                        paramsSerializer: (params) => {
                            return qs.stringify(params, {allowDots: true})
                        },
                        responseType: "blob",
                    }
                )
            ).pipe(
                map((response: AxiosResponse) => {
                    const filename = extractFileName(response)
                    return saveExportDisplayData(response.data, filename)
                }),
                catchError((err) => of(errorAction(err)))
            )
        )
    )
const loadTotalNumberOfSamplesEpic: Epic = (action$, state$) =>
    action$.pipe(
        ofType(getType(loadTotalNumberOfSamplesAction)),
        withLatestFrom(state$),
        switchMap(() => from(axios.get<number>(`/api/totalSamples`))),
        map((response) => {
            return response.data
        }),
        map((number) => {
            return loadTotalNumberOfSamplesSuccessAction(number)
        }),
        catchError((error, caught) => merge(of(errorAction(error)), caught))
    )
export default combineEpics(
    loadSpeciesData,
    loadMaterialsData,
    loadAssayData,
    loadMetabolitesData,
    loadMetabolitesExternalData,
    loadClassesData,
    loadReferenceRangesData,
    loadReferenceRangesInternalData,
    loadDetectabilitySummaryData,
    loadDateTableData,
    loadVariablesData,
    loadProjectsData,
    loadMatrices,
    loadConfiguration,
    exportDisplayData,
    exportDetectabilitySummary,
    loadValuesData,
    cancelReferenceRangesData,
    loadExistingVariables,
    loadTotalNumberOfSamplesEpic,
    loadDemographicsData
)
